/**
 * useUserTab - Manages all state and logic for the Users tab.
 *
 * Reads / writes UI state through the `useUserManagementStore` Zustand
 * store and composes it with data-fetching (TanStack Query) and
 * derived computations (filtered rows).
 *
 * @returns User tab state and handlers
 *
 * @example
 * ```ts
 * const users = useUserTab();
 * // users.usersData?.content — rows from server (with filters applied)
 * // users.handleApplyFilters() — apply draft filters
 * ```
 *
 * @module features/users/hooks/useUserTab
 */
import { useCallback, useMemo } from 'react';

import { useShallow } from 'zustand/react/shallow';
import { useBusinessAccountUsers } from '@/features/users/hooks/useBusinessAccountUsers';
import { useUserManagementStore } from '@/features/users/store';

export const useUserTab = () => {
  // ── Store ──────────────────────────────────────────────────────
  const {
    usersPage,
    usersPageSize,
    userFiltersOpen: filtersOpen,
    appliedUserFilters: appliedFilters,
    draftUserFilters: draftFilters,
    setUsersPage,
    setUsersPageSize,
    setDraftUserFilters: setDraftFilters,
    openUserFilters: handleOpenFilters,
    closeUserFilters: handleCloseFilters,
    applyUserFilters: handleApplyFilters,
    clearUserFilters: handleClearFilters,
  } = useUserManagementStore(
    useShallow((s) => ({
      usersPage: s.usersPage,
      usersPageSize: s.usersPageSize,
      userFiltersOpen: s.userFiltersOpen,
      appliedUserFilters: s.appliedUserFilters,
      draftUserFilters: s.draftUserFilters,
      setUsersPage: s.setUsersPage,
      setUsersPageSize: s.setUsersPageSize,
      setDraftUserFilters: s.setDraftUserFilters,
      openUserFilters: s.openUserFilters,
      closeUserFilters: s.closeUserFilters,
      applyUserFilters: s.applyUserFilters,
      clearUserFilters: s.clearUserFilters,
    }))
  );

  // ── Data fetching ──────────────────────────────────────────────
  const {
    data: usersData,
    isLoading: usersLoading,
    isRefetching: usersRefetching,
    refetch: refetchUsers,
    error: usersError,
  } = useBusinessAccountUsers({
    offset: usersPage * usersPageSize,
    limit: usersPageSize,
    ...(appliedFilters.email ? { email: appliedFilters.email.trim() } : {}),
    ...(appliedFilters.profileName
      ? { profileName: appliedFilters.profileName }
      : {}),
  });

  const usersUnauthorized = Boolean(
    usersError &&
    'response' in usersError &&
    (usersError as { response?: { status?: number } }).response?.status === 401
  );

  // ── Active filter count (derived) ─────────────────────────────
  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (appliedFilters.email && appliedFilters.email.trim().length > 0)
      count += 1;
    if (appliedFilters.profileName) count += 1;
    return count;
  }, [appliedFilters]);

  // ── Refresh (re-fetch the current page) ───────────────────────
  const handleRefresh = useCallback(() => {
    void refetchUsers();
  }, [refetchUsers]);

  return {
    // Data
    usersData,
    usersLoading,
    usersError,
    usersUnauthorized,
    isRefreshing: usersRefetching,
    handleRefresh,

    // Pagination
    usersPage,
    usersPageSize,
    setUsersPage,
    setUsersPageSize,

    // Filters
    filtersOpen,
    draftFilters,
    setDraftFilters,
    appliedFilters,
    activeFiltersCount,
    handleOpenFilters,
    handleCloseFilters,
    handleClearFilters,
    handleApplyFilters,
  } as const;
};
